import type { Task } from "~skillBuilder/utils/types";

import { image, link, title } from "../../contentBlocks";

export const PSchool = {
  name: "Professionalism in the School Setting",
  uniqueName: "p-school",
  icon: "Lesson",
  durationTimeText: "10 min",
  steps: [
    // Step 1
    {
      nextButtonText: "Start",
      questions: [
        // Question 1.1
        {
          attribute: "professionalism.school/overview",
          label: "",
          // description: "",
          answerType: {
            type: "CONTENT",
            value: "valid",
            markdown: `
${title({ text: "Professionalism in the School Setting", durationTimeText: "10 min" })}
<p>Your attitude, appearance, and actions shape how students, staff, and administrators perceive you. Demonstrating professionalism through confidence, respect, and preparedness not only sets a positive example but also fosters a productive and respectful learning environment. How you present yourself—from your demeanor to your dress—plays a key role in establishing credibility and ensuring a smooth, successful day in the classroom.</p>
<div align="center">
<div style="max-width:250px;width:100%;">
${image({ url: "https://res.cloudinary.com/swingeducation/image/upload/fl_preserve_transparency/v1758202464/skillbuilderAssets/optimized/Man_adjusting_tie.jpg", alt: "A man fixing his tie." })}
</div>
</div>
            `,
          },
        },
      ],
    },
    // Step 2
    {
      nextButtonText: "Next",
      questions: [
        // Question 2.1
        {
          attribute: "professionalism.school/role",
          label: "The role of professionalism in substitute teaching",
          // description: "",
          answerType: {
            type: "CONTENT",
            value: "valid",
            markdown: `
Professionalism in substitute teaching goes beyond simply following the lesson plan. It means demonstrating respect, reliability, and dedication to ensuring students continue learning and thriving, even in their teacher's absence. A professional approach fosters a positive, constructive atmosphere and helps you fulfill your responsibilities effectively.
            `,
          },
        },
      ],
    },
    // Step 3
    {
      nextButtonText: "Next",
      questions: [
        // Question 3.1
        {
          attribute: "professionalism.school/image",
          label: "Professional image",
          // description: "",
          answerType: {
            type: "CONTENT",
            value: "valid",
            markdown: `
Schools look for these key qualities in a substitute's professional demeanor:

<ul style="margin-left: 16px;">
<li>✅ <strong>Punctuality & Preparedness</strong> – Arrive on time, ready to lead the class.</li>
<li>👕 <strong>Appropriate Attire</strong> – Dress professionally and follow the school's dress code.</li>
<li>😊 <strong>Respectful & Positive Attitude</strong> – Your demeanor sets the tone for the day.</li>
<li>📜 <strong>Knowledge of School Policies</strong> – Be aware of attendance, discipline, and emergency procedures—or be willing to learn.</li>
<li>🗣️ <strong>Skilled Communication</strong> – Speak clearly, listen actively, and provide concise instructions.</li>
<li>🔄 <strong>Adaptability</strong> – Be flexible; school dynamics can change quickly.</li>
<li>🎯 <strong>Model Professionalism</strong> – Avoid use of your phone or other personal electronic device. Never use inappropriate language, or unprofessional conduct on campus.</li>
</ul>
            `,
          },
        },
      ],
    },
    // Step 4
    {
      nextButtonText: "Next",
      questions: [
        // Question 4.1
        {
          attribute: "professionalism.school/impact",
          label: "The impact of professionalism",
          // description: "",
          answerType: {
            type: "CONTENT",
            value: "valid",
            markdown: `
Your professional mindset directly influences the classroom environment, student engagement, and school culture. Maintaining professionalism builds strong relationships with students and staff, leading to future opportunities within the school community.
            `,
          },
        },
      ],
    },
    // Step 5
    {
      nextButtonText: "Next Lesson",
      questions: [
        // Question 5.1
        {
          attribute: "professionalism.school/first-impression",
          label: "Making a great first impression",
          // description: "",
          answerType: {
            type: "CONTENT",
            value: "valid",
            markdown: `
Your first impression can be shaped by how you enter a new school and how you present yourself on your Swing profile.

💡 **In Person:** Arrive on time, dress professionally, and greet staff and students with a positive attitude. Be prepared, set clear expectations, and stay actively engaged. Leave personal electronic devices stowed with your belongings to prevent distractions.

💡 **Online:** Before stepping into a school, update your Swing profile with:
<ul style="margin-left: 16px;">
<li>📸 A professional photo</li>
<li>📝 A concise, relevant bio about your teaching experience and passion</li>
<li>🎓 Educational background & qualifications</li>
</ul>

${link({ text: "How do I set up my Swing profile?", url: "https://support.swingeducation.com/hc/en-us/articles/115006437688-How-do-I-setup-my-Swing-teacher-profile" })}
            `,
          },
        },
      ],
    },
  ],
} as const satisfies Task;
